/**
 * Participant Portal API Service
 * Participant-facing endpoints for dashboard data
 */

import apiClient from './client.js';
import type { OneOnOneType } from '../../types';

// ============================================================================
// Types
// ============================================================================

export interface ParticipantCohort {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  group_id: string | null;
  group_name: string | null;
  partner_name: string | null;
}

export type ModalityType = 'group_coaching' | 'one_on_one' | 'workshop';

export interface ParticipantModality {
  id: string;
  modality_type: ModalityType;
  session_count: number | null;
  completed_count: number;
}

export interface ParticipantUpcomingSession {
  id: string;
  title: string;
  session_type: string;
  scheduled_start: string;
  scheduled_end: string | null;
  zoom_link: string | null;
  duration_minutes: number | null;
  group_name: string | null;
  modality_type: string;
  /** MED-1350: set on a 1:1 row; a check-in reads 'intro_checkin' or 'midpoint_checkin'. */
  one_on_one_type?: OneOnOneType | null;
  /** MED-1350: the session's own coach, or null. */
  coach_name?: string | null;
  /**
   * MED-1045 — true when the viewer is the active session leader
   * (peer_leader `confirmed_leader` / `backup_assigned`). The "Starting Soon"
   * banner uses this to show "Prepare to start" (→ reflection/prep) instead of
   * "Enter waiting room", since a leader is never routed to the waiting room.
   * Only the `starting_soon` payload is enriched today, so this is optional.
   */
  is_leading?: boolean;
}

/** MED-699: 'intro' | 'midpoint_checkin' produce dedicated action items;
 *  null / undefined means 1:1 coaching session (pre-MED-699 behavior). */
export type ParticipantHuddleType = 'intro' | 'midpoint_checkin' | 'general';

export interface ParticipantActionItem {
  coach_id: string;
  first_name: string;
  last_name: string;
  unscheduled_count: number;
  /** MED-699: null → 1:1 coaching (default), 'intro' / 'midpoint_checkin' → typed check-in */
  huddle_type: ParticipantHuddleType | null;
  /** MED-845: true when the action item's coach has connected Calendly. Drives the
   *  Schedule CTA's disabled state and the "Awaiting Coach Schedule" badge. */
  coach_calendly_connected: boolean;
}

export type OneOnOneSessionStatus = 'unscheduled' | 'scheduled' | 'completed' | 'cancelled';

export interface ParticipantOneOnOneSession {
  id: string;
  session_number: number | null;
  status: OneOnOneSessionStatus;
  scheduled_start: string | null;
  scheduled_end: string | null;
  duration_minutes: number | null;
  video_link: string | null;
  scheduling_method: 'manual' | 'calendly' | 'calendly_fallback' | null;
  one_on_one_type: OneOnOneType | null;
  /** MED-1350: the session's own coach. It can differ from the section coach. */
  coach_id?: string | null;
  coach_name?: string | null;
  /** MED-1365 round 4 review: the coach a reschedule books with. */
  coach?: ParticipantOneOnOneCoach | null;
}

export interface ParticipantOneOnOneCoach {
  id: string;
  first_name: string;
  last_name: string;
  job_title: string | null;
  avatar_url: string | null;
  email: string | null;
  calendly_connected: boolean;
}

export interface ParticipantOneOnOneCoaching {
  coach: ParticipantOneOnOneCoach | null;
  session_count: number;
  scheduled_count: number;
  sessions: ParticipantOneOnOneSession[];
  default_meeting_link: string | null;
}

export type WorkshopAttendanceStatus =
  | 'pending'
  | 'present'
  | 'absent'
  | 'excused'
  | 'late'
  | 'attended'
  | 'partial';

export interface ParticipantWorkshopResource {
  id: string;
  title: string;
  original_filename: string | null;
  resource_type: 'file' | 'link';
  url: string | null;
  mime_type: string | null;
  file_size: number | null;
}

export interface ParticipantWorkshop {
  id: string;
  title: string;
  session_number: number | null;
  sequence_number: number | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  duration_minutes: number | null;
  timezone: string;
  facilitator_name: string | null;
  attendance_status: WorkshopAttendanceStatus | null;
  resources: ParticipantWorkshopResource[];
}

// ============================================================================
// Group Coaching Types (E22-3)
// ============================================================================

export interface ParticipantGroupCoachingSession {
  id: string;
  title: string;
  session_number: number | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  duration_minutes: number | null;
  status: string;
  attendance_percent: number | null;
  avg_rating: number | null;
}

export interface ParticipantGroupCoachingCoach {
  id: string;
  first_name: string;
  last_name: string;
  job_title: string | null;
  avatar_url: string | null;
  email: string | null;
  calendly_connected: boolean;
  checkin_event_configured: boolean;
}

export interface ParticipantActiveCheckin {
  id: string;
  status: string;
  scheduled_start: string | null;
  video_link: string | null;
  /** MED-1365 QA round 4: 'calendly_fallback' is a Google Calendar invite. */
  scheduling_method?: 'calendly' | 'calendly_fallback' | null;
}

/** MED-699: Typed check-in placeholder (Intro / Midpoint) surfaced in participant timeline. */
export interface ParticipantTypedCheckin {
  id: string;
  huddle_type: 'intro' | 'midpoint_checkin';
  /** Session status: unscheduled | scheduled | completed | cancelled */
  status: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  /** Only present for midpoint — the session index AFTER which the row should render. */
  midpoint_session_index: number | null;
  video_link: string | null;
  /** MED-1365 QA round 4: 'calendly_fallback' is a Google Calendar invite. */
  scheduling_method?: 'manual' | 'calendly' | 'calendly_fallback' | null;
  /** MED-1350: who runs the check-in, and its length (20 minutes when unset). */
  coach_id?: string | null;
  coach_name?: string | null;
  duration_minutes?: number | null;
  /** MED-1365 round 4 review: the coach a reschedule books with. */
  coach?: ParticipantGroupCoachingCoach | null;
}

export interface ParticipantGroupCoaching {
  group_id: string;
  group_name: string;
  coach: ParticipantGroupCoachingCoach | null;
  sessions: ParticipantGroupCoachingSession[];
  active_checkin: ParticipantActiveCheckin | null;
  can_schedule_checkin: boolean;
  /** MED-699: Typed Intro/Midpoint placeholders. Either may be null if E6-10a didn't run for this cohort. */
  typed_checkins: {
    intro: ParticipantTypedCheckin | null;
    midpoint: ParticipantTypedCheckin | null;
  };
}

/**
 * MED-950: A session currently in `in_progress` status that the participant is
 * enrolled in. Surfaced on the dashboard regardless of modality announcement
 * status so a launched session is always joinable.
 */
export interface ParticipantLiveSession {
  id: string;
  title: string;
  session_type: string;
  modality_type: string | null;
  group_name: string | null;
  scheduled_start: string | null;
  actual_start: string | null;
  duration_minutes: number | null;
}

/** MED-813 / E4.1 — nearest upcoming peer-led session this participant leads. */
export interface ParticipantPeerLeaderCallout {
  id: string;
  title: string;
  session_number: number | null;
  scheduled_start: string;
  group_name: string | null;
}

// ============================================================================
// MED-938 / E3.5 — Participant Dashboard Refresh (two-column design).
// Present (non-null) only when the `live_session_delivery` flag is on. Peer-
// leader state mirrors the E4.1 pre-session page (single source of truth).
// ============================================================================

/** E4.1 peer-leader role for a session (State 7 added in MED-1168). */
export type PeerLeaderState =
  | 'confirmed_leader'
  | 'backup_needed'
  | 'volunteer_pending'
  | 'another_assigned'
  | 'backup_assigned'
  | 'declined'
  // MED-1168 — you're still the assigned leader but declined; not "leading"
  // (is_leading stays false) until you re-confirm on the pre-session page.
  | 'leader_declined_self';

export interface DashboardRefreshUpcomingSession {
  id: string;
  /** Raw `sessions.title` — workshop/standalone fallback for the label. */
  title: string;
  /** MED-1146 — display-aware resolved topic (nullable); FE composes the label. */
  session_topic: string | null;
  session_number: number | null;
  session_type: string;
  modality_type: string | null;
  scheduled_start: string;
  scheduled_end: string | null;
  duration_minutes: number | null;
  group_name: string | null;
  /**
   * MED-1062 (E4.0h) — underlying session status ('scheduled' | 'in_progress' |
   * 'missed'). A 'missed' peer-led session is relabeled "PENDING RESCHEDULE"
   * with all CTAs suppressed; everything else renders normally.
   */
  status: string;
  is_live: boolean;
  /**
   * MED-1225 — the session is happening now and is joinable: `in_progress` at
   * any time (AC5), OR `scheduled` from its start until its estimated end
   * (AC2/AC3). Always true when is_live. Drives the participant "Join session"
   * CTA (→ waiting room) for a not-yet-started ongoing session. A `missed`
   * session is never ongoing.
   */
  is_ongoing: boolean;
  join_link: string | null;
  /** Reflection availability gates the Needs Attention prompt; far-future sessions are unavailable. */
  reflection_available: boolean;
  reflection_complete: boolean;
  peer_leader_state: PeerLeaderState | null;
  /** State 1 or 5 → Leading badge + Start Session Prep CTA. */
  is_leading: boolean;
  /** Leader prep not yet completed (drives the prompt; the Leading badge persists regardless). */
  prep_outstanding: boolean;
  /**
   * MED-1144 (QA follow-up) — leader prep is reachable (its sequence gate is
   * open). Gates the leader-prep entry points so they never offer a prep page
   * that would reject access as "locked". Always true for non-leading sessions.
   */
  prep_unlocked: boolean;
}

export interface DashboardRefreshCompletedSession {
  id: string;
  /** Raw `sessions.title` — workshop/standalone fallback for the label. */
  title: string;
  /** MED-1146 — display-aware resolved topic (nullable); FE composes the label. */
  session_topic: string | null;
  session_number: number | null;
  modality_type: string | null;
  scheduled_start: string | null;
  duration_minutes: number | null;
  /** MED-1146 — group name for the no-topic fallback label (Session N: {group}). */
  group_name: string | null;
  /**
   * MED-1145 — cohort this session belongs to; used to build the cohort-scoped
   * peer-leader post-session route for the "return to post-session" item.
   */
  cohort_id: string;
  /**
   * MED-1145 — true when the viewer led this session (parity with the coach
   * `session_report_ready` item). Gates the leader-only return path (AC4).
   */
  is_leading: boolean;
  attended: boolean;
  /**
   * MED-1063 — true when the participant was explicitly marked `absent`. The
   * feedback prompt gates on "not absent" (NOT "is attended"), so partial
   * attendees still qualify (Decision 1).
   */
  is_absent: boolean;
  /**
   * MED-1240 — resolved attendance status for the meta-line label:
   * `attended` | `partial` | `absent` | `pending`, or `null` when no attendance
   * row exists yet (un-recorded). Drives the full label mapping in metaLine();
   * `attended`/`is_absent` remain the feedback-nudge gates (AC6).
   */
  attendance_status: string | null;
  /**
   * MED-1063 (AC2) — true once the calendar day AFTER the session has begun
   * (session timezone, computed server-side). Gates the deferred feedback
   * prompt; does not affect page availability.
   */
  feedback_prompt_available: boolean;
  /** E3.4 — true when the participant submitted post-session feedback. */
  feedback_complete: boolean;
}

export interface DashboardRefreshProgram {
  name: string;
  partner_name: string | null;
  sessions_attended: number;
  sessions_total: number;
  next_milestone_label: string | null;
}

export interface DashboardRefreshCohortManager {
  name: string;
  role_label: string;
}

export interface DashboardRefreshGroup {
  name: string;
  schedule_label: string | null;
  member_count: number;
  next_session_date: string | null;
}

export interface DashboardRefreshModalityProgress {
  modality_type: string;
  completed_count: number;
  session_count: number | null;
}

export interface DashboardRefreshResource {
  id: string;
  title: string;
  resource_type: string;
  mime_type: string | null;
  original_filename: string | null;
  url: string | null;
}

export interface ParticipantDashboardRefresh {
  is_peer_led: boolean;
  upcoming_sessions: DashboardRefreshUpcomingSession[];
  completed_sessions: DashboardRefreshCompletedSession[];
  program: DashboardRefreshProgram | null;
  cohort_manager: DashboardRefreshCohortManager | null;
  group: DashboardRefreshGroup | null;
  modalities: DashboardRefreshModalityProgress[];
  resources: DashboardRefreshResource[];
}

export interface ParticipantDashboardData {
  cohort: ParticipantCohort | null;
  announced_modalities: ParticipantModality[];
  upcoming_sessions: ParticipantUpcomingSession[];
  starting_soon: ParticipantUpcomingSession | null;
  live_session: ParticipantLiveSession | null;
  action_items: ParticipantActionItem[];
  one_on_one_coaching: ParticipantOneOnOneCoaching | null;
  workshops: ParticipantWorkshop[] | null;
  group_coaching: ParticipantGroupCoaching | null;
  /** MED-813 — peer-leader callout, or null when the participant leads nothing upcoming. */
  peer_leader_callout: ParticipantPeerLeaderCallout | null;
  /** MED-938 / E3.5 — two-column refresh data; null when live_session_delivery is off. */
  refresh: ParticipantDashboardRefresh | null;
  /**
   * MED-1181 — the cohort's group-coaching per-modality Live Session Delivery
   * gate. LSD affordances (live-session / starting-soon / peer-leader banners,
   * the refresh layout, the "Reflect" pre-session link) must AND this in on top
   * of the global flag. Defaults false for non-LSD (classic MED01) cohorts.
   */
  live_session_delivery_enabled: boolean;
}

// ============================================================================
// API Methods
// ============================================================================

export interface ParticipantCohortListItem {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
  partner_name: string | null;
  // MED-778: Feature toggle state
  participant_features_enabled: boolean;
  // MED-1307 (MDF-184): effective participant app access (features AND app access)
  app_access_enabled: boolean;
}

export interface ScheduleSessionBody {
  scheduled_start: string;
  timezone: string;
  meeting_link?: string;
  notes?: string;
}

export interface ScheduleCheckinBody {
  cohort_id: string;
  scheduled_start: string;
  timezone: string;
  notes?: string;
  /** MED-699: 'general' (default — new record) vs 'intro' / 'midpoint_checkin' (update placeholder) */
  huddle_type?: ParticipantHuddleType;
}

export interface CalendlyAvailableSlot {
  status: 'available' | 'invitees_remain';
  invitees_remaining: number;
  start_time: string;
  end_time: string;
  scheduling_url: string;
}

export interface CoachAvailabilityResponse {
  slots: CalendlyAvailableSlot[];
  connected: boolean;
  event_type_configured: boolean;
}

/** MED-1365 QA round 4: the query part that names a session being moved. */
function rescheduleQuery(sessionId: string | undefined): string {
  return sessionId ? `&reschedule_session_id=${encodeURIComponent(sessionId)}` : '';
}

export const participantPortalApi = {
  getCohorts: (): Promise<ParticipantCohortListItem[]> =>
    apiClient.get<ParticipantCohortListItem[]>('/api/participant/cohorts'),

  getDashboard: (cohortId?: string): Promise<ParticipantDashboardData> =>
    apiClient.get<ParticipantDashboardData>(
      cohortId ? `/api/participant/dashboard?cohort_id=${cohortId}` : '/api/participant/dashboard',
    ),

  scheduleSession: (
    sessionId: string,
    body: ScheduleSessionBody,
  ): Promise<{ scheduled: boolean; session_id: string; scheduling_method: string; calendly_fallback?: boolean }> =>
    apiClient.post<{ scheduled: boolean; session_id: string; scheduling_method: string; calendly_fallback?: boolean }>(
      `/api/participant/sessions/${sessionId}/schedule`,
      body,
    ),

  /**
   * MED-1365 QA round 4: rescheduleSessionId is the booked session that the
   * participant moves, so the times do not hide its own slot.
   */
  getCoachAvailability: (
    coachId: string,
    startTime: string,
    endTime: string,
    durationMinutes?: number,
    rescheduleSessionId?: string,
  ): Promise<CoachAvailabilityResponse> =>
    apiClient.get<CoachAvailabilityResponse>(
      `/api/participant/coaches/${coachId}/availability?start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}${durationMinutes ? `&duration_minutes=${durationMinutes}` : ''}${rescheduleQuery(rescheduleSessionId)}`,
    ),

  getCheckinCoachAvailability: (
    coachId: string,
    startTime: string,
    endTime: string,
    rescheduleSessionId?: string,
  ): Promise<CoachAvailabilityResponse> =>
    apiClient.get<CoachAvailabilityResponse>(
      `/api/participant/coaches/${coachId}/checkin-availability?start_time=${encodeURIComponent(startTime)}&end_time=${encodeURIComponent(endTime)}${rescheduleQuery(rescheduleSessionId)}`,
    ),

  scheduleCheckin: (
    body: ScheduleCheckinBody,
  ): Promise<{ scheduled: boolean; session_id: string; scheduling_method: string; calendly_fallback?: boolean }> =>
    apiClient.post<{ scheduled: boolean; session_id: string; scheduling_method: string; calendly_fallback?: boolean }>(
      '/api/participant/check-ins/schedule',
      body,
    ),

  cancelSession: (
    sessionId: string,
  ): Promise<{ canceled: boolean; session_id: string; new_status: string }> =>
    apiClient.post<{ canceled: boolean; session_id: string; new_status: string }>(
      `/api/participant/sessions/${sessionId}/cancel`,
      {},
    ),

  rescheduleSession: (
    sessionId: string,
    body: Omit<ScheduleSessionBody, 'meeting_link'>,
  ): Promise<{ rescheduled: boolean; session_id: string; scheduling_method: string; calendly_fallback?: boolean }> =>
    apiClient.post<{ rescheduled: boolean; session_id: string; scheduling_method: string; calendly_fallback?: boolean }>(
      `/api/participant/sessions/${sessionId}/reschedule`,
      body,
    ),
};
